import { useEffect } from "react";
import { Box, Text, Button, Layer, Heading } from "grommet";
import { StatusGood, StatusWarning, StatusCritical, FormClose,} from "grommet-icons";


// These two functions work together to ensure text remains readable on any background color:
// getRGB(colorName) converts a given color name or code into its RGB (red, green, blue) components.
// getContrastBackground uses those values to calculate brightness 
//      and returns a contrasting color (black or white) to ensure readability on the given background.


// Converts a color name to its RGB values
const getRGB = (colorName) => {
    if (!colorName) return null;
  
    const ctx = document.createElement("canvas").getContext("2d");
  
    try {
      ctx.fillStyle = colorName;
      const computed = ctx.fillStyle;
  
      ctx.fillStyle = computed;
      const rgbMatch = ctx.fillStyle.match(/\d+/g);
      if (!rgbMatch || rgbMatch.length < 3) return null;
  
      const [r, g, b] = rgbMatch.map(Number).slice(0, 3);
      return { r, g, b };
    } catch {
      return null;
    }
  };
  
  // Chooses a contrasting background color (light or dark) for better readability
  const getContrastBackground = (colorName = "white") => {
    const rgb = getRGB(colorName);
    if (!rgb) return { color: "black", opacity: "weak" };
  
    const luminance =
      0.2126 * (rgb.r / 255) +
      0.7152 * (rgb.g / 255) +
      0.0722 * (rgb.b / 255);
  
    const isLight = luminance > 0.6;
    return {
      color: isLight ? "black" : "white",
      opacity: "medium",
    };
  };

const Notification = ({
    status = "normal",
    time = 2000, // Delay 2 seconds
    color = "white",
    title, // optional title
    message,
    onClose,
}) => {
    
    const statusConfig = {
        normal: {
            defaultTitle: "Success",
            icon: <StatusGood color={color} />,
            // background: { color: "status-ok", opacity: "strong" },
            background: "#039039"
        },
        warning: {
            defaultTitle: "Warning",
            icon: <StatusWarning color={color} />,
            background: "status-warning"
        },
        critical: {
            defaultTitle: "Error",
            icon: <StatusCritical color={color} />,
            background: "status-critical"
        },
        default: {
            defaultTitle: "Notification",
            icon: null,
            background: "#0077b6", // soft blue
        },
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (onClose) onClose();
        }, time);
        return () => clearTimeout(timer);
    }, [time, onClose]);

    const currentStatus = statusConfig[status] ? status : "default";
    const { defaultTitle, icon, background } = statusConfig[currentStatus];

    const dynamicBackground = getContrastBackground(color);
    const finalTitle = title || defaultTitle;

    return (
        <Layer
            position="top"
            modal={false}
            margin={{ vertical: "medium", horizontal: "small" }}
            onEsc={onClose}
            onClickOutside={() => {}}
            responsive={false}
            plain
        >
            <Box
                width="medium"
                round="small"
                elevation="large"
                background={background}
                pad="medium"
                gap="small"
                border={{ color: color, size: "xsmall" }}
            >
                <Box direction="row" justify="between" align="center">
                    <Box direction="row" gap="small" align="center">
                        {icon}
                        <Heading
                            level={4}
                            margin="none"
                            color={color}
                            style={{ fontWeight: 600 }}
                        >
                        {finalTitle}
                        </Heading>
                    </Box>
                    <Button
                        hoverIndicator
                        icon={<FormClose color={color} />}
                        onClick={onClose}
                        style={{
                            border: "none",
                            boxShadow: "none",
                            padding: 0,
                            minWidth: "auto", // prevents default spacing
                          }}
                    />
                </Box>

                <Box
                    background={dynamicBackground}
                    pad="small"
                    round="xsmall"
                    margin={{ top: 'small' }}
                >
                    <Text color={color} size="small">
                        {message}
                    </Text>
                </Box>
            </Box>
        </Layer>
    );
};

export default Notification;